import React, { createContext, useRef, useEffect, useMemo } from 'react';
import { useGlobalData } from './globalData';
import { useAPIUserRefreshToken, useAPIUserDetail } from 'repo/user/authRepo';
import {
    useAPIUserRefreshToken as useAPIAdminRefreshToken,
    useAPIUserDetail as useAPIAdminDetail,
} from 'repo/admin/authRepo';
import { useAPIUserRefreshToken as useAPISystemRefreshToken } from 'repo/system/authRepo';
import { useCookies } from 'react-cookie';
import { getJSONItem } from 'utils/manageLocalStorage';
import { authTypeConstants } from 'constants/auth';
import { usePathData } from './pathData';

// Auth Context
export const AuthContext = createContext([{}, () => {}]);

export const buildAuthData = (
    authData = {
        authType: null,
        token: null,
        tokenExpiresIn: null,
        user: null,
        isAuthenticated: null,
    }
) => ({
    authType: authData.authType ?? null,
    token: authData.token ?? null,
    tokenExpiresIn: authData.tokenExpiresIn ?? null,
    user: authData.user ?? null,
    isAuthenticated: authData.isAuthenticated ?? null,
});

// useAuth Hook
export const useAuth = () => {
    const [{ auth: _auth }, updateGlobalData] = useGlobalData();
    const appPath = usePathData();
    const [cookies, setCookie, removeCookie] = useCookies(['token']);

    // Clear shadow cookies across likely paths (same cookie name can exist with different Path)
    const clearTokenCookies = () => {
        const base = appPath?.base || '';
        const clientBase = appPath?.clientId ? `/${appPath.clientId}` : '';
        const pathsToClear = ['/', clientBase, base, `${base}/login`].filter(Boolean);
        pathsToClear.forEach(path => removeCookie('token', { path }));
    };

    // auth based on app path
    const auth = useMemo(() => {
        return _auth && _auth.authType !== appPath.userType ? buildAuthData() : _auth;
    }, [_auth, appPath]);

    // LOGOUT
    const logout = () => {
        // Clear Auth Data
        updateGlobalData('auth', buildAuthData());
        clearTokenCookies();
    };

    // Set Auth Data
    const setAuth = arg => {
        const { authType, token, tokenExpiresIn, user, isAuthenticated: _isAuthenticated } =
            arg ?? {};

        // set true if token & user not null
        const isAuthenticated = _isAuthenticated ?? (token != null && user != null);

        const authData = buildAuthData({ authType, token, tokenExpiresIn, user, isAuthenticated });

        clearTokenCookies();

        if (token != null) {
            setCookie('token', token, { path: '/' });
        }

        updateGlobalData('auth', authData);
    };

    // Update partial attributes
    const updateAuth = obj => {
        const newAuth = Object.assign(auth, obj);
        setAuth(newAuth);
    };

    // Update partial attributes of user
    const updateAuthUser = obj => {
        setAuth({ ...auth, user: { ...auth.user, ...obj } });
    };

    // Get Token from Local Storage
    // & Resync Auth Data with Cookie if needed
    const getToken = () => {
        if (cookies.token != auth.token) {
            // resync from storage
            const localAuth = getJSONItem('auth');
            setAuth(localAuth);
        }
        return auth.token;
    };

    // Handle 401 event (unauthorized)
    const handle401 = () => {
        logout();
    };

    return { logout, auth, setAuth, updateAuth, updateAuthUser, getToken, handle401 };
};

/**
 * AuthProvider React Component
 *
 * @param {any} props
 * @return JSX
 */
export function AuthProvider(props) {
    const { auth, setAuth } = useAuth();
    const [refreshUserToken] = useAPIUserRefreshToken();
    const [refreshAdminToken] = useAPIAdminRefreshToken();
    const [refreshSystemToken] = useAPISystemRefreshToken();

    // Fetch user detail hooks for user and admin auth types
    const [fetchUserDetail] = useAPIUserDetail();
    const [fetchAdminDetail] = useAPIAdminDetail();

    // Track if user data has been fetched to prevent multiple calls
    const hasFetchedUserRef = useRef(false);
    const lastAuthTypeRef = useRef(null);

    // Refresh token function
    const refreshRef = useRef(() => {});
    refreshRef.current = () => {
        if (auth.authType == authTypeConstants.USER) {
            refreshUserToken();
        } else if (auth.authType == authTypeConstants.ADMIN) {
            refreshAdminToken();
        } else if (auth.authType == authTypeConstants.SYSTEM) {
            refreshSystemToken();
        }
    };

    // check expire
    useEffect(() => {
        if (auth.tokenExpiresIn) {
            const now = new Date().getTime();
            if (now < auth.tokenExpiresIn) {
                // schedule at 1 minute ahead (60 seconds)
                const nextRefreshAt = auth.tokenExpiresIn - now - 60000;
                setTimeout(() => {
                    refreshRef.current();
                }, Math.max(nextRefreshAt, 0));
            }
        }
    }, [auth.tokenExpiresIn]);

    // Fetch fresh user data when authenticated (similar to ClientDataProvider)
    // Reset fetch flag when auth type changes or user logs out
    useEffect(() => {
        if (!auth.isAuthenticated || auth.authType !== lastAuthTypeRef.current) {
            hasFetchedUserRef.current = false;
            lastAuthTypeRef.current = auth.authType;
        }
    }, [auth.isAuthenticated, auth.authType]);

    useEffect(() => {
        // Only fetch if authenticated, has token, no user yet (e.g. page refresh with token), and hasn't been fetched yet.
        if (auth.isAuthenticated && auth.token && !auth.user && !hasFetchedUserRef.current) {
            hasFetchedUserRef.current = true;

            if (auth.authType === authTypeConstants.USER) {
                fetchUserDetail()
                    .then(res => {
                        // Update user data from response
                        if (res?.data?.data) {
                            setAuth({
                                ...auth,
                                user: res.data.data,
                            });
                        }
                    })
                    .catch(() => {
                        // Silently fail - user data will remain from localStorage
                        hasFetchedUserRef.current = false; // Allow retry on next mount
                    });
            } else if (auth.authType === authTypeConstants.ADMIN) {
                fetchAdminDetail()
                    .then(res => {
                        // Update user data from response
                        if (res?.data?.data) {
                            setAuth({
                                ...auth,
                                user: res.data.data,
                            });
                        }
                    })
                    .catch(() => {
                        // Silently fail - user data will remain from localStorage
                        hasFetchedUserRef.current = false; // Allow retry on next mount
                    });
            }
        }
    }, [auth.isAuthenticated, auth.authType, auth.token, auth.user]);

    return <AuthContext.Provider {...props} />;
}
